import { Avatar, Box, IconButton, styled, Typography, CircularProgress } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MessageIcon from '@mui/icons-material/Message';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const ButtonsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  zIndex: 1,
  //@ts-ignore
  backgroundColor: theme.palette.background.panel,
}));

const SmallIconButton = styled(IconButton)({
  padding: 4,
});

const HeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '16px',
  position: 'relative',
});

const ContactAvatar = styled(Avatar)({
  width: 60,
  height: 60,
  marginBottom: 8,
});

const ContactName = styled(Typography)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 200,
  textAlign: 'center',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 60,
  height: 60,
  marginBottom: 8,
});

type HeaderProps = {
  imgUrl?: string;
  name?: string;
  contactId?: string;
  navigate: (path: string) => void;
  isLoading?: boolean;
  onError?: (error: Error) => void;
  onClose: () => void;
  isBusiness?: boolean;
};

const Header = ({
  imgUrl,
  name,
  contactId,
  navigate,
  isLoading = false,
  onError,
  onClose,
  isBusiness = false,
}: HeaderProps) => {
  const handleNavigation = useCallback(
    (url: string) => (event: React.MouseEvent) => {
      try {
        event.preventDefault();
        event.stopPropagation();
        if (!url) {
          throw new Error('Navigation URL is required');
        }

        const isNewTab = event.ctrlKey || event.metaKey || event.button === 1; // Ctrl+Click, Cmd+Click, Middle Mouse

        if (isNewTab) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          navigate(url);
        }
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Navigation failed'));
      }
    },
    [navigate, onError],
  );

  const { t } = useTranslation();

  const handleNavigateToContacts = handleNavigation(
    isBusiness ? `/a/contacts/business/${contactId}` : `/a/contacts/contacts/${contactId}`,
  );
  const handleNavigateToConversations = handleNavigation(
    `/a/conversations/conversations?contactId=${contactId}`,
  );

  const hasName = Boolean(name);
  const displayName = name || t('contactName.noName');
  const avatarAlt = `Avatar for ${displayName}`;

  return (
    <HeaderContainer>
      <ButtonsContainer>
        <SmallIconButton
          color="info"
          size="small"
          onClick={(e) => {
            handleNavigateToContacts(e);
            onClose();
          }}
          onMouseDown={(e) => {
            if (e.button === 1) {
              // Middle mouse button
              handleNavigateToContacts(e);
              onClose();
            }
          }}
          disabled={!contactId}
          aria-label="View contact details"
        >
          <ArrowForwardIcon fontSize="small" />
        </SmallIconButton>
        <SmallIconButton
          color="info"
          size="small"
          onClick={(e) => {
            handleNavigateToConversations(e);
            onClose();
          }}
          onMouseDown={(e) => {
            if (e.button === 1) {
              // Middle mouse button
              handleNavigateToConversations(e);
              onClose();
            }
          }}
          aria-label="Go to conversations"
        >
          <MessageIcon fontSize="small" />
        </SmallIconButton>
      </ButtonsContainer>
      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={24} />
        </LoadingContainer>
      ) : (
        <ContactAvatar
          src={imgUrl}
          alt={avatarAlt}
          onError={() => onError?.(new Error('Failed to load avatar'))}
        />
      )}
      <ContactName
        variant="h6"
        title={displayName}
        sx={!hasName ? { color: 'text.disabled', fontStyle: 'italic' } : undefined}
      >
        {displayName}
      </ContactName>
    </HeaderContainer>
  );
};

export default Header;
